/**
 * データの書き出し（エクスポート）。
 *
 * **契約者が自分のデータを持ち出す唯一の経路。** API（申請・一覧・
 * ダウンロード）は揃っていたのに叩く画面が無く、解約前にデータを
 * 持ち出す手段が事実上「運営に依頼する」しか無かった。
 * 個人情報保護法 33 条（保有個人データの開示は「電磁的記録の提供」を含む）
 * への対応としても、ここまでが 1 セットになる。
 *
 * 生成は非同期（`202 Accepted`）で、実体は worker が S3 へ置く。
 * ダウンロードは `GET /exports/:id/download` が返す**署名付き URL**で、
 * レスポンスそのものはファイルではない（JSON をそのまま保存すると
 * 「開けないファイルが落ちてくる」になる）。
 *
 * 生成物は 7 日で失効する（個人情報を残し続けないため）。期限を
 * 画面に必ず出す。出さないと「昨日の書き出しが消えた」に見える。
 */
import * as React from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Alert,
  AlertDescription,
  AlertTitle,
  Badge,
  Button,
  Card,
  CardContent,
  CursorPagination,
  DataTable,
  Field,
  PageHeader,
  Section,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  StatusBadge,
  formatBytes,
  formatDateTime,
  useCursorPagination,
  type DataTableColumn,
  formatCount,
} from '@mail-sv/ui';
import { Download, FileDown } from 'lucide-react';
import { ORG_PERMISSION } from '@mail-sv/shared';
import { api, downloadFile } from '../lib/api.js';
import { listApi } from '../lib/content-api.js';
import { hasPermission, useAuth } from '../stores/auth.js';
import { qk } from '../lib/query-keys.js';
import { useOrg } from '../lib/hooks.js';
import { ErrorAlert } from '../components/async.js';
import { FormAlert } from '../components/form.js';
import { HelpNote } from '../components/help.js';
import { openPendingTab } from '../lib/open-tab.js';
import type { CursorPage, ExportJob, ExportKind } from '../lib/api-types.js';

/** 書き出せる種類。**プラン機能で出し分けるのは監査ログだけ**（API と同じ判定）。 */
const KIND_LABEL: Record<ExportKind, string> = {
  audit: '操作の履歴（監査ログ）',
  usage: '利用状況（容量・送信数）',
  mailboxes: 'メールアドレスの一覧',
  invoices: '請求書の一覧',
};

const KIND_HELP: Record<ExportKind, string> = {
  audit: 'この組織で行われた設定変更・ログインなどの記録です。',
  usage: '日ごとの使用容量と送信数です。',
  mailboxes: 'メールアドレス・容量・状態の一覧です。メールの本文は含みません。',
  invoices: '発行済みの請求書の一覧です。請求書 PDF は請求画面から取得できます。',
};

/**
 * この画面**以外**から作られる書き出しの訳語。
 *
 * `export_jobs` にはメールボックス詳細の mbox 書き出し（`maildir`）と、
 * 宛先の vCard 持ち出し（`contacts_vcard`）も入ってくる。訳語表に無いと
 * 一覧に生の識別子（`maildir`）がそのまま並ぶ。
 */
const OTHER_KIND_LABEL: Record<string, string> = {
  maildir: 'メールの本文（mbox）',
  // `GET /contacts/vcard` が残す持ち出しの記録（`export_jobs.kind = 'contacts'`）。
  contacts: '宛先の連絡先（vCard）',
  /*
   * メールアドレス一覧から申し込む一括の設定情報シート（PDF・W-ONBOARD §3-3）。
   * **この画面の選択肢には出さない**（どのアドレスぶんを刷るかを選ぶ必要が
   * あり、その選択は一覧の側にしか無いため）。ここは受け取りだけを担う。
   */
  setup_sheets: 'メール設定情報シート（PDF）',
};

/** 一覧の 1 行に出す種別名。未知の種別は識別子をそのまま出す（隠さない）。 */
function kindLabel(kind: string): string {
  return (
    (KIND_LABEL as Record<string, string>)[kind] ?? OTHER_KIND_LABEL[kind] ?? kind
  );
}

export function ExportsPage() {
  const org = useOrg();
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const page = useCursorPagination(20);
  const [kind, setKind] = React.useState<ExportKind>('mailboxes');
  const [format, setFormat] = React.useState<'csv' | 'json'>('csv');
  const [downloading, setDownloading] = React.useState<string | null>(null);
  const [downloadError, setDownloadError] = React.useState<unknown>(null);

  const auditAllowed = org.data?.plan.features.auditExport ?? false;

  const list = useQuery({
    queryKey: qk.exports({ cursor: page.cursor, limit: page.limit }),
    queryFn: () => {
      const params = new URLSearchParams({ limit: String(page.limit) });
      if (page.cursor) params.set('cursor', page.cursor);
      return api.get<CursorPage<ExportJob>>(`/exports?${params.toString()}`);
    },
    // 生成中のものがあるうちは自動で追いかける（押し直させない）。
    refetchInterval: (query) =>
      (query.state.data?.items ?? []).some((e) => e.status === 'queued' || e.status === 'running')
        ? 5000
        : false,
  });

  /**
   * 宛先の vCard 書き出し（R56）。`GET /contacts/vcard` は
   * **JSON ではなく `text/vcard` をそのまま返す**同期 API なので、
   * `downloadFile()`（Bearer 付きの fetch → Blob）で受ける。
   * `<a href>` では認証ヘッダが載らず 401 になる。
   */
  const canReadContacts = hasPermission(user, ORG_PERMISSION.contactRead);
  const [vcardListId, setVcardListId] = React.useState('all');
  const [vcardBusy, setVcardBusy] = React.useState(false);
  const [vcardError, setVcardError] = React.useState<unknown>(null);

  const lists = useQuery({
    queryKey: qk.contactLists(),
    queryFn: () => listApi.list(),
    enabled: canReadContacts,
  });

  const downloadVcard = React.useCallback(async () => {
    setVcardBusy(true);
    setVcardError(null);
    try {
      const path =
        vcardListId === 'all'
          ? '/contacts/vcard'
          : `/contacts/vcard?listId=${encodeURIComponent(vcardListId)}`;
      await downloadFile(path, 'contacts.vcf');
    } catch (e) {
      setVcardError(e);
    } finally {
      setVcardBusy(false);
    }
  }, [vcardListId]);

  const request = useMutation({
    mutationFn: () => api.post<{ exportId: string; status: string }>('/exports', { kind, format }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['portal', 'exports'] as const });
    },
  });

  /**
   * ダウンロード。**署名付き URL へ遷移させる。**
   * ポップアップブロック対策で、クリックと同じイベントループで空タブを開く。
   */
  const download = React.useCallback(async (job: ExportJob) => {
    setDownloading(job.id);
    setDownloadError(null);
    const tab = openPendingTab();
    try {
      const link = await api.get<{ url: string; expiresAt: string; objectKey: string }>(
        `/exports/${encodeURIComponent(job.id)}/download`,
      );
      tab.navigate(link.url);
    } catch (e) {
      tab.close();
      setDownloadError(e);
    } finally {
      setDownloading(null);
    }
  }, []);

  const columns: Array<DataTableColumn<ExportJob>> = [
    {
      id: 'kind',
      header: '内容',
      cell: (row) => (
        <div className="min-w-0">
          <p className="text-sm font-medium">{kindLabel(row.kind)}</p>
          <p className="text-xs text-muted-foreground">
            {row.format.toUpperCase()} / 申請 {formatDateTime(row.createdAt)}
          </p>
        </div>
      ),
    },
    {
      id: 'status',
      header: '状態',
      width: 'w-32',
      /*
       * 状態の色（`toneOf`）は shared が唯一の出典なので生値を渡す。
       * 訳語だけは契約者向けの言い回し（「成功」ではなく「書き出し済み」）に
       * 差し替える。
       */
      cell: (row) => (
        <StatusBadge
          status={row.status}
          label={
            row.status === 'succeeded'
              ? '書き出し済み'
              : row.status === 'failed'
                ? '失敗'
                : '準備中'
          }
          size="sm"
          dot
        />
      ),
    },
    {
      id: 'size',
      header: '件数 / サイズ',
      width: 'w-36',
      hideBelow: 'md',
      cell: (row) => (
        <span className="tabular text-xs">
          {row.rowCount === null ? '—' : `${formatCount(row.rowCount)} 件`}
          {row.byteSize === null ? '' : ` / ${formatBytes(row.byteSize)}`}
        </span>
      ),
    },
    {
      id: 'expires',
      header: '取得期限',
      width: 'w-40',
      hideBelow: 'lg',
      cell: (row) => (
        <span className="tabular text-xs">
          {row.expiresAt ? formatDateTime(row.expiresAt) : '—'}
        </span>
      ),
    },
    {
      id: 'actions',
      header: '',
      width: 'w-32',
      align: 'right',
      cell: (row) =>
        row.status === 'succeeded' ? (
          <Button
            variant="ghost"
            size="sm"
            onClick={() => void download(row)}
            loading={downloading === row.id}
          >
            <Download aria-hidden />
            取得
          </Button>
        ) : null,
    },
  ];

  return (
    <>
      <PageHeader
        title="データの書き出し"
        description="ご契約中のデータを CSV / JSON で書き出せます。解約前のお持ち出しにもご利用ください。"
      />

      <div className="space-y-5">
        <Section title="書き出しを申請する">
          <Card>
            <CardContent className="space-y-4 p-5">
              <FormAlert error={request.error} />
              {request.isSuccess ? (
                <Alert variant="success">
                  <AlertTitle>書き出しを受け付けました</AlertTitle>
                  <AlertDescription>
                    準備ができると下の一覧に「書き出し済み」と表示されます（数分かかることがあります）。
                  </AlertDescription>
                </Alert>
              ) : null}

              <div className="grid gap-4 sm:grid-cols-2">
                <Field label="書き出す内容" hint={KIND_HELP[kind]}>
                  <Select value={kind} onValueChange={(v) => setKind(v as ExportKind)}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {(Object.keys(KIND_LABEL) as ExportKind[]).map((k) => (
                        <SelectItem key={k} value={k} disabled={k === 'audit' && !auditAllowed}>
                          {KIND_LABEL[k]}
                          {k === 'audit' && !auditAllowed ? '（上位プラン）' : ''}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </Field>

                <Field label="形式" hint="表計算ソフトで開くなら CSV を選んでください。">
                  <Select value={format} onValueChange={(v) => setFormat(v as 'csv' | 'json')}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="csv">CSV</SelectItem>
                      <SelectItem value="json">JSON</SelectItem>
                    </SelectContent>
                  </Select>
                </Field>
              </div>

              {kind === 'audit' && !auditAllowed ? (
                <Alert variant="warning">
                  <AlertTitle>ご利用中のプランでは監査ログを書き出せません</AlertTitle>
                  <AlertDescription>
                    操作の履歴の書き出しは上位プランの機能です。プランの変更をご検討ください。
                  </AlertDescription>
                </Alert>
              ) : null}

              <Button
                onClick={() => request.mutate()}
                loading={request.isPending}
                disabled={kind === 'audit' && !auditAllowed}
              >
                <FileDown aria-hidden />
                書き出しを申請する
              </Button>
            </CardContent>
          </Card>
        </Section>

        {/*
         * **宛先の vCard 書き出し（`GET /contacts/vcard`）。**
         *
         * 上の非同期エクスポートとは別系統で、その場で本文が返る同期 API。
         * 名簿は第三者の個人データなので持ち出しは監査に残る（サーバ側で
         * `audit_logs` と `export_jobs` の両方に記録される）。
         * 画面にもそのことを書いておく。
         */}
        {canReadContacts ? (
          <Section title="宛先を vCard で書き出す">
            <Card>
              <CardContent className="space-y-4 p-5">
                <p className="text-sm text-muted-foreground">
                  登録されている宛先を vCard（.vcf）形式で書き出します。アドレス帳ソフトや
                  スマートフォンの連絡先へそのまま取り込めます。
                </p>
                {vcardError ? <ErrorAlert error={vcardError} /> : null}
                <Field
                  label="書き出す範囲"
                  hint="宛先が 50,000 件を超える場合は、リストを指定して分けて書き出してください。"
                >
                  <Select value={vcardListId} onValueChange={setVcardListId}>
                    <SelectTrigger className="max-w-md">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">すべての宛先</SelectItem>
                      {(lists.data?.items ?? []).map((l) => (
                        <SelectItem key={l.id} value={l.id}>
                          {l.name}（購読中 {l.counts.subscribed} 件）
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </Field>
                <Button variant="outline" onClick={() => void downloadVcard()} disabled={vcardBusy}>
                  <Download aria-hidden />
                  {vcardBusy ? '準備しています…' : 'vCard を書き出す'}
                </Button>
                <p className="text-xs text-muted-foreground">
                  書き出した記録は操作の履歴に残ります（第三者の個人データをお預かりしているため）。
                </p>
              </CardContent>
            </Card>
          </Section>
        ) : null}

        <Section title="書き出しの履歴">
          {downloadError ? <ErrorAlert error={downloadError} /> : null}
          <DataTable
            columns={columns}
            rows={list.data?.items ?? []}
            rowKey={(row) => row.id}
            loading={list.isPending}
            error={list.isError ? '書き出しの履歴を取得できませんでした。' : undefined}
            emptyTitle="書き出しはまだありません"
            emptyDescription="上のフォームから申請すると、ここに表示されます。"
            footer={
              <CursorPagination
                nextCursor={list.data?.nextCursor ?? null}
                canGoPrev={page.canGoPrev}
                onNext={() => page.goNext(list.data?.nextCursor ?? null)}
                onPrev={page.goPrev}
                limit={page.limit}
                onLimitChange={page.setLimit}
                currentCount={list.data?.items.length}
                loading={list.isFetching}
              />
            }
          />
          <HelpNote title="保存期間について">
            書き出したファイルは{' '}
            <Badge variant="muted" size="sm">
              7 日間
            </Badge>{' '}
            で自動的に削除されます（個人データを必要以上に保持しないためです）。期限が過ぎた場合は、
            もう一度申請してください。メールの本文そのものは、この書き出しには含まれません
            （本文はメールソフトや Webメールから取得してください）。
          </HelpNote>
        </Section>
      </div>
    </>
  );
}
